import {
  Backdrop,
  CircularProgress,
  Grid,
  Paper,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Filters } from './components/filters';
import HeaderCard from './components/header-card';
import { ModalAudit } from './components/modal-audit';
import { ModalAuthTransfer } from './components/modal-auth-transfer/index';
import { ModalCreateWire } from './components/modal-create-wire';
import Nothing from './components/nothing';
import { TableWireTransfers } from './components/table-wire-transfers';
import useWireTransfers from './wire-transfers-hooks';
import { useSubscribeToSocketEvent } from 'hooks/useSubscribeToSocketEvent';
import { InfoDeliveryType } from 'components/users/user-wire-transfers/types';

const useStyles = makeStyles((theme: any) => ({
  paperContainer: {
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    borderRadius: theme.spacing(1),
    margin: `${theme.spacing(2.75)} ${theme.spacing(2.5)}`,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  contentContainer: {
    padding: theme.spacing(4),
    gap: theme.spacing(4)
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
}));

type Props = {
  idTitleCompany: string
}

export const WireTransfers: FC<Props> = ({ idTitleCompany }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    transferPages,
    currentPage,
    totalPages,
    rowsPerPage,
    totalRows,
    setCurrentPage,
    changeRowsPerPage,
    open,
    handleToggleModals,
    members,
    handleCreateWire,
    handleAuthTransfer,
    openBackdrop,
    onFilterChange,
    onSort,
    onUpdateAmountLimits,
    clearFilters,
    isDataLoading,
    disbursments,
    selecteDisbursments,
    handleChangeDisbursements,
    wireDataList,
    setWireDataList,
    referencesRef,
    paginateTransfers
  } = useWireTransfers({ idTitleCompany });

  useSubscribeToSocketEvent({
    eventKey: 'info-delivery',
    callback: (data: any) => {
      const { data_id, document } = data;

      if (document?.wire?.length && !wireDataList.some((wire) => wire.data_id === data_id)) {
        setWireDataList((prev) => [
          ...prev,
          {
            data_id,
            wire: document.wire.map((wire) => {
              return {
                ...wire,
                description: `${wire.name}, ${wire.bank}, account ***${wire.account.slice(
                  -3
                )}, routing ***${wire.routing.slice(-3)}`,
              };
            }),
          },
        ]);
      } else {
        const referenceIndex = referencesRef.current.findIndex(
          ({ reference }) => reference === data.reference
        );
        if (referenceIndex >= 0) {
          referencesRef.current[referenceIndex].document = data.document;

          const currentCentury = Math.floor(new Date().getFullYear() / 100) * 100;
          const longYear =
            (Number(data.reference.slice(0, 2)) <= 20 ? currentCentury + 100 : currentCentury) +
            Number(data.reference.slice(0, 2));
          referencesRef.current[referenceIndex].date = new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
          }).format(
            new Date(`${longYear}/${data.reference.slice(2, 4)}/${data.reference.slice(4, 6)}`)
          );

          referencesRef.current[referenceIndex].amount =
            data.document.instructions.reduce((acc, element) => acc + element.amount, 0) || 0;
          referencesRef.current[referenceIndex].isDataLoading = false;
        }

        paginateTransfers({
          transfers: referencesRef.current,
          transfersPerPage: rowsPerPage,
        });
      }
    },
  });
  return (
    <>
      <Paper className={classes.paperContainer}>
        <HeaderCard t={t} handleToggleModals={handleToggleModals} />
        <Grid container className={classes.contentContainer} direction="column">
          <Grid item>
            <Filters
              t={t}
              onFilterChange={onFilterChange}
              onSort={onSort}
              onUpdateAmountLimits={onUpdateAmountLimits}
              clearFilters={clearFilters}
            />
          </Grid>
          {transferPages.length > 0 && (
            <Grid item>
              <TableWireTransfers
                t={t}
                wireTransferPages={transferPages}
                isDataLoading={isDataLoading}
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalPages={totalPages}
                totalRows={totalRows}
                setCurrentPage={setCurrentPage}
                changeRowsPerPage={changeRowsPerPage}
                handleToggleModals={handleToggleModals}
              />
            </Grid>
          )}
        </Grid>
        <Backdrop open={openBackdrop} className={classes.backdrop}>
          <CircularProgress color="primary" />
        </Backdrop>
      </Paper>
      {transferPages.length === 0 && <Nothing t={t} />}
      {open.location === 'createWire' && (
        <ModalCreateWire
          t={t}
          open={open.status}
          members={members}
          handleToggleModals={handleToggleModals}
          handleCreateWire={handleCreateWire}
          disbursments={disbursments}
          selecteDisbursments={selecteDisbursments}
          handleChangeDisbursements={handleChangeDisbursements}
          wireDataList={wireDataList}
        />
      )}
      {open.location === 'authTransfer' && open.data && (
        <ModalAuthTransfer
          transfer={open.data}
          open={open.status}
          t={t}
          handleToggleModals={handleToggleModals}
          handleAuthTransfer={handleAuthTransfer}
        />
      )}
      {open.location === "audit" && open.data && (
        <ModalAudit
          transfer={open.data}
          onClose={handleToggleModals}
        />
      )}
    </>
  );
};
